import React, { useEffect, useState } from "react";
import moment from "moment";
import Select from "react-select";
import { formatJamKerja } from "../../../util/function";
import { bulan, tahun, BagianConstant } from "../../../constant";
import DeleteIcon from "../../../asset/delete_icon.png";
import PopUpComponent from "../../../component/PopUpComponent/PopUpComponent";
import "./ListRequest.css";
import { useListRequest } from "./useListRequest";

const DB_DATE_FORMAT = "ddd MMM D YYYY";
const INPUT_DATE_FORMAT = "YYYY-MM-DD";

const STATUS_OPTIONS = [
  { value: "", label: "Belum ada status" },
  { value: "Diterima", label: "Diterima" },
  { value: "Ditolak", label: "Ditolak" },
];

// Tipe request yang punya opsi "Bayar"
const BAYAR_TYPES = ["Izin", "Sakit", "SetengahHari"];
const canBayar = (type) => BAYAR_TYPES.includes(type);

const dbDateToInput = (value) => {
  if (!value) return "";
  const m = moment(value, [DB_DATE_FORMAT, INPUT_DATE_FORMAT], "en");
  return m.isValid() ? m.format(INPUT_DATE_FORMAT) : "";
};

const inputDateToDb = (value) =>
  value
    ? moment(value, INPUT_DATE_FORMAT).locale("en").format(DB_DATE_FORMAT)
    : "";

const displayDate = (value) =>
  value ? moment(value).utcOffset("+0700").format("DD-MM-YYYY") : "-";

const formatDuration = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h && m) return `${h} jam ${m} menit`;
  if (h) return `${h} jam`;
  return `${m} menit`;
};

// Warna badge disamakan dengan legenda di halaman Absensi
const typeClass = (row) => {
  const t = (row.type || "").toLowerCase();
  if (["izin", "sakit", "alpha"].includes(t)) return `lr-badge lr-badge--${t}`;
  if (row.typeRequest === "Request") return "lr-badge lr-badge--request";
  return "lr-badge lr-badge--other";
};

const statusClass = (status) => {
  if (status === "Diterima") return "lr-badge lr-badge--hadir";
  if (status === "Ditolak") return "lr-badge lr-badge--alpha";
  return "";
};

const EditTimeModal = ({ row, onClose, onSave }) => {
  const [form, setForm] = useState({
    tanggalMasuk: "",
    jamMasuk: "",
    tanggalKeluar: "",
    jamKeluar: "",
    status: "",
    isBayar: false,
  });
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!row) return;
    const init = {
      tanggalMasuk: dbDateToInput(row.tanggalMasuk),
      jamMasuk: row.jamMasuk || "",
      tanggalKeluar: dbDateToInput(row.tanggalKeluar),
      jamKeluar: row.jamKeluar || "",
      status: row.status || "",
      isBayar: !!row.isBayar,
    };
    setForm(init);
    setInitial(init);
    setSaving(false);
  }, [row]);

  if (!row || !initial) return null;

  const set = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const setBayar = (e) =>
    setForm((prev) => ({ ...prev, isBayar: e.target.checked }));

  const timeChanged = [
    "tanggalMasuk",
    "jamMasuk",
    "tanggalKeluar",
    "jamKeluar",
  ].some((k) => form[k] !== initial[k]);
  const statusChanged = form.status !== initial.status;
  const bayarChanged = canBayar(row.type) && form.isBayar !== initial.isBayar;

  const hasKeluar = form.tanggalKeluar || form.jamKeluar;
  const start = moment(
    `${form.tanggalMasuk} ${form.jamMasuk}`,
    "YYYY-MM-DD HH:mm",
    true
  );
  const end = moment(
    `${form.tanggalKeluar} ${form.jamKeluar}`,
    "YYYY-MM-DD HH:mm",
    true
  );

  let error = "";
  let durationText = "";
  if (timeChanged) {
    if (!form.tanggalMasuk || !form.jamMasuk) {
      error = "Tanggal dan jam masuk wajib diisi.";
    } else if (hasKeluar && (!form.tanggalKeluar || !form.jamKeluar)) {
      error = "Lengkapi tanggal dan jam keluar, atau kosongkan keduanya.";
    } else if (hasKeluar && start.isValid() && end.isValid()) {
      const diff = end.diff(start, "minutes");
      if (diff <= 0) {
        error =
          "Waktu keluar harus setelah waktu masuk. Untuk shift malam, ganti tanggal keluar ke hari berikutnya.";
      } else {
        durationText = formatDuration(diff);
      }
    }
  }

  const nothingChanged = !timeChanged && !statusChanged && !bayarChanged;

  const handleSave = async () => {
    if (error || saving || nothingChanged) return;
    setSaving(true);

    const payload = { requestId: row.requestId };
    if (statusChanged) payload.status = form.status;
    if (bayarChanged) payload.isBayar = form.isBayar;
    if (timeChanged) {
      payload.tanggalMasuk = inputDateToDb(form.tanggalMasuk);
      payload.jamMasuk = form.jamMasuk;
      payload.tanggalKeluar = inputDateToDb(form.tanggalKeluar);
      payload.jamKeluar = form.jamKeluar;
    }

    const ok = await onSave(payload);
    setSaving(false);
    if (ok !== false) onClose();
  };

  return (
    <div className="lr-overlay" onClick={onClose}>
      <div
        className="lr-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Edit request"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="lr-modal__title">Edit request</h3>

        <div className="lr-modal__summary">
          <b>{row.name}</b>
          <span>
            {row.bagian || "-"} &middot;{" "}
            <span className={typeClass(row)}>{row.type}</span>
          </span>
          {row.keterangan && <span className="lr-muted">{row.keterangan}</span>}
        </div>

        <label className="lr-modal__status">
          Status
          <select value={form.status} onChange={set("status")}>
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        {canBayar(row.type) && (
          <label
            className="lr-modal__status"
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              className="lr-check"
              checked={form.isBayar}
              onChange={setBayar}
            />
            Dibayar
          </label>
        )}

        <div className="lr-modal__grid">
          <fieldset className="lr-modal__group lr-modal__group--in">
            <legend>Masuk</legend>
            <label>
              Tanggal
              <input
                type="date"
                value={form.tanggalMasuk}
                onChange={set("tanggalMasuk")}
              />
            </label>
            <label>
              Jam
              <input
                type="time"
                value={form.jamMasuk}
                onChange={set("jamMasuk")}
              />
            </label>
          </fieldset>

          <fieldset className="lr-modal__group lr-modal__group--out">
            <legend>Keluar</legend>
            <label>
              Tanggal
              <input
                type="date"
                value={form.tanggalKeluar}
                onChange={set("tanggalKeluar")}
              />
            </label>
            <label>
              Jam
              <input
                type="time"
                value={form.jamKeluar}
                onChange={set("jamKeluar")}
              />
            </label>
          </fieldset>
        </div>

        {error ? (
          <p className="lr-modal__msg lr-modal__msg--error">{error}</p>
        ) : (
          durationText && (
            <p className="lr-modal__msg">Durasi: {durationText}</p>
          )
        )}

        {statusChanged && (
          <p className="lr-modal__msg lr-muted">
            Catatan: mengubah status hanya mengubah data request ini, tidak
            menghitung ulang jam kerja karyawan.
          </p>
        )}

        {bayarChanged && row.type === "SetengahHari" && form.isBayar && (
          <p className="lr-modal__msg lr-muted">
            Request setengah hari yang dibayar akan dihitung 1 hari penuh di
            rekap absensi.
          </p>
        )}

        <div className="lr-modal__actions">
          <button
            type="button"
            className="lr-btn lr-btn--ghost"
            onClick={onClose}
          >
            Batal
          </button>
          <button
            type="button"
            className="lr-btn lr-btn--primary"
            disabled={!!error || saving || nothingChanged}
            onClick={handleSave}
          >
            {saving ? "Menyimpan..." : "Simpan perubahan"}
          </button>
        </div>
      </div>
    </div>
  );
};

const RequestTable = ({ rows, onChecklist, onDelete, onEdit }) => {
  if (!rows || rows.length === 0) {
    return (
      <div className="lr-empty">Belum ada data request untuk filter ini.</div>
    );
  }

  return (
    <table className="lr-table">
      <thead>
        <tr>
          <th>No.</th>
          <th>Nama</th>
          <th>Bagian</th>
          <th>In</th>
          <th>Out</th>
          <th>Type</th>
          <th>Status</th>
          <th>
            Jam Lebih
            <br />
            Sebelum
          </th>
          <th>Security</th>
          <th>Keterangan</th>
          <th>
            Tidak
            <br />
            Dihitung
          </th>
          <th>Bayar</th>
          <th>Inap</th>
          <th>SD</th>
          <th>Aksi</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((data, index) => (
          <tr key={data.requestId || index}>
            <td className="lr-center">{index + 1}.</td>
            <td className="lr-name">{data.name}</td>
            <td>{data.bagian}</td>
            <td>
              <div className="lr-time lr-time--in">
                <span>{displayDate(data.tanggalMasuk)}</span>
                <b>{data.jamMasuk}</b>
              </div>
            </td>
            <td>
              <div className="lr-time lr-time--out">
                <span>{displayDate(data.tanggalKeluar)}</span>
                <b>{data.jamKeluar}</b>
              </div>
            </td>
            <td>
              <span className={typeClass(data)}>{data.type}</span>
            </td>
            <td>
              {data.status ? (
                <span className={statusClass(data.status)}>{data.status}</span>
              ) : (
                <span className="lr-muted">-</span>
              )}
            </td>
            <td>{formatJamKerja(data.jamLebihPast)}</td>
            <td>{data.approver}</td>
            <td
              className={data.type === "Alpha" ? "lr-danger" : ""}
              style={{ minWidth: "240px" }}
            >
              {(data?.keterangan || "").toUpperCase()}
            </td>

            <td className="lr-center">
              {data.type === "Izin" && (
                <input
                  type="checkbox"
                  className="lr-check"
                  checked={!!data.tidakDihitung}
                  onChange={() =>
                    onChecklist({
                      requestId: data.requestId,
                      tidakDihitung: !data.tidakDihitung,
                    })
                  }
                />
              )}
            </td>

            <td className="lr-center">
              {canBayar(data.type) && (
                <input
                  type="checkbox"
                  className="lr-check"
                  checked={!!data.isBayar}
                  onChange={() =>
                    onChecklist({
                      requestId: data.requestId,
                      isBayar: !data.isBayar,
                    })
                  }
                />
              )}
            </td>

            <td className="lr-center">
              {data.type === "Sakit" && (
                <input
                  type="checkbox"
                  className="lr-check check-orange"
                  checked={!!data.isInap}
                  onChange={() =>
                    onChecklist(
                      data.isInap
                        ? { requestId: data.requestId, isInap: false }
                        : {
                            requestId: data.requestId,
                            isInap: true,
                            checked: false,
                          }
                    )
                  }
                />
              )}
            </td>

            <td className="lr-center">
              {data.type !== "Ambil" &&
                data.type !== "Tambah" &&
                data.type !== "Kerja" && (
                  <input
                    type="checkbox"
                    className="lr-check"
                    checked={!!data.checked}
                    onChange={() =>
                      onChecklist(
                        data.checked
                          ? { requestId: data.requestId, checked: false }
                          : {
                              requestId: data.requestId,
                              checked: true,
                              isInap: false,
                            }
                      )
                    }
                  />
                )}
            </td>

            <td>
              <div className="lr-actions">
                <button
                  type="button"
                  className="lr-icon-btn"
                  title="Edit"
                  aria-label={`Edit ${data.name}`}
                  onClick={() => onEdit(data)}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="lr-icon-btn lr-icon-btn--danger"
                  title="Hapus"
                  aria-label={`Hapus request ${data.name}`}
                  onClick={() => onDelete(data.requestId)}
                >
                  <img src={DeleteIcon} alt="" width="18" height="18" />
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const ListRequest = () => {
  const {
    filterFrom,
    setFilterFrom,
    filterUntil,
    setFilterUntil,
    paramSearch,
    setParamSearch,
    filterType,
    setFilterType,
    dataTemp,
    subMenu,
    setSubMenu,
    searchBar,
    setSearchBar,
    filterBulan,
    setFilterBulan,
    filterTahun,
    setFilterTahun,
    kepalaBagian,
    setKepalaBagian,
    uniqueNames,
    filteredData,
    handleDeleteRequest,
    filterFromRekap,
    setFilterFromRekap,
    filterUntilRekap,
    setFilterUntilRekap,
    handleExportRekapitulasi,
    setDataChecklist,
    popUpFilterRange,
    setPopUpFilterRange,
    handleFindRange,
    reFormat,
    operatorPick,
    setOperatorPick,
    listFilterRange,
    filterBagian,
    setFilterBagian,
    handleEditRequestTime,
  } = useListRequest();

  const [editRow, setEditRow] = useState(null);

  const multipleSortListRequest = dataTemp
    ? [...dataTemp].sort((a, b) => {
        if (new Date(a.tanggalMasuk) < new Date(b.tanggalMasuk)) return -1;
        if (new Date(a.tanggalMasuk) > new Date(b.tanggalMasuk)) return 1;
        if (a.type < b.type) return -1;
        if (a.type > b.type) return 1;
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      })
    : [];

  return (
    <>
      <EditTimeModal
        row={editRow}
        onClose={() => setEditRow(null)}
        onSave={handleEditRequestTime}
      />

      {popUpFilterRange && (
        <PopUpComponent
          style={{
            marginTop: "calc(100vh - 95vh - 20px)",
            height: "90vh",
            width: "88%",
            overflowY: "auto",
            padding: "16px",
          }}
        >
          <div className="lr-popup">
            <h3 className="lr-popup__title">Filter range per operator</h3>

            <div className="lr-toolbar">
              <label className="lr-field">
                <span>Dari</span>
                <input
                  type="date"
                  value={filterFrom}
                  onChange={(e) => setFilterFrom(e.target.value)}
                />
              </label>
              <label className="lr-field">
                <span>Sampai</span>
                <input
                  type="date"
                  value={filterUntil}
                  onChange={(e) => setFilterUntil(e.target.value)}
                />
              </label>
              <div className="lr-field lr-field--grow">
                <span>Operator</span>
                <Select
                  placeholder="Pilih operator"
                  options={reFormat}
                  value={operatorPick || ""}
                  onChange={setOperatorPick}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>
            </div>

            <div className="lr-table-wrap lr-table-wrap--popup">
              <RequestTable
                rows={listFilterRange}
                onChecklist={setDataChecklist}
                onDelete={handleDeleteRequest}
                onEdit={setEditRow}
              />
            </div>

            <div className="lr-popup__footer">
              <button
                type="button"
                className="lr-btn lr-btn--ghost"
                onClick={() => {
                  setPopUpFilterRange(false);
                  setOperatorPick();
                }}
              >
                Tutup
              </button>
              <button
                type="button"
                className="lr-btn lr-btn--primary"
                onClick={() => handleFindRange()}
              >
                Cari
              </button>
            </div>
          </div>
        </PopUpComponent>
      )}

      <div className="container-whitelist">
        <div className="wrapper-card-whitelist">
          <div className="lr-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={subMenu === "Request"}
              className={`lr-tab ${subMenu === "Request" ? "is-active" : ""}`}
              onClick={() => setSubMenu("Request")}
            >
              Request
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={subMenu === "Rekap"}
              className={`lr-tab ${subMenu === "Rekap" ? "is-active" : ""}`}
              onClick={() => setSubMenu("Rekap")}
            >
              Rekapitulasi
            </button>
          </div>

          {subMenu === "Request" ? (
            <>
              <div className="lr-toolbar">
                <label className="lr-field">
                  <span>Tanggal</span>
                  <input
                    type="date"
                    value={filterFrom}
                    onChange={(e) => setFilterFrom(e.target.value)}
                  />
                </label>

                <label className="lr-field">
                  <span>Tipe request</span>
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <option value="Pilih Type">Semua tipe</option>
                    <option value="Request">Request</option>
                    <option value="TambahJam">Tambah / Ambil Jam</option>
                  </select>
                </label>

                <label className="lr-field">
                  <span>Bagian</span>
                  <select
                    value={filterBagian}
                    onChange={(e) => setFilterBagian(e.target.value)}
                  >
                    <option value="Pilih Bagian">Semua bagian</option>
                    {BagianConstant.map((obj) => (
                      <option key={obj.value} value={obj.value}>
                        {obj.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="lr-field lr-field--grow">
                  <span>Cari nama</span>
                  <input
                    type="text"
                    placeholder="Ketik nama karyawan"
                    value={paramSearch}
                    onChange={(e) => setParamSearch(e.target.value)}
                  />
                </label>

                <button
                  type="button"
                  className="lr-btn lr-btn--outline"
                  onClick={() => setPopUpFilterRange(true)}
                >
                  Filter range
                </button>
              </div>

              <div className="lr-table-wrap">
                <RequestTable
                  rows={multipleSortListRequest}
                  onChecklist={setDataChecklist}
                  onDelete={handleDeleteRequest}
                  onEdit={setEditRow}
                />
              </div>
            </>
          ) : (
            <>
              <div className="lr-toolbar">
                <label className="lr-field lr-field--grow">
                  <span>Cari nama</span>
                  <input
                    type="text"
                    placeholder="Ketik nama karyawan"
                    value={searchBar || ""}
                    onChange={(e) => setSearchBar(e.target.value)}
                  />
                </label>
                <label className="lr-field">
                  <span>Bulan</span>
                  <select
                    value={filterBulan}
                    onChange={(e) => setFilterBulan(e.target.value)}
                  >
                    {bulan.map((ele) => (
                      <option key={ele.value} value={ele.value}>
                        {ele.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="lr-field">
                  <span>Tahun</span>
                  <select
                    value={filterTahun}
                    onChange={(e) => setFilterTahun(e.target.value)}
                  >
                    {tahun.map((ele) => (
                      <option key={ele} value={ele}>
                        {ele}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="lr-field">
                  <span>Kepala bagian</span>
                  <select
                    value={kepalaBagian}
                    onChange={(e) => setKepalaBagian(e.target.value)}
                  >
                    <option value="Pilih Kepala">Semua kepala</option>
                    {uniqueNames &&
                      uniqueNames.map((ele) => (
                        <option key={ele} value={ele}>
                          {ele}
                        </option>
                      ))}
                  </select>
                </label>
                <label className="lr-field">
                  <span>Dari</span>
                  <input
                    type="date"
                    value={filterFromRekap}
                    onChange={(e) => setFilterFromRekap(e.target.value)}
                  />
                </label>
                <label className="lr-field">
                  <span>Sampai</span>
                  <input
                    type="date"
                    value={filterUntilRekap}
                    onChange={(e) => setFilterUntilRekap(e.target.value)}
                  />
                </label>

                <button
                  type="button"
                  className="lr-btn lr-btn--ghost"
                  onClick={() => {
                    setKepalaBagian("Pilih Kepala");
                    setSearchBar("");
                    setFilterFromRekap("");
                    setFilterUntilRekap("");
                  }}
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="lr-btn lr-btn--primary"
                  onClick={() => handleExportRekapitulasi()}
                >
                  Export Excel
                </button>
              </div>

              <div className="lr-table-wrap">
                {filteredData && filteredData.length > 0 ? (
                  <table className="lr-table">
                    <thead>
                      <tr>
                        <th rowSpan={2}>No.</th>
                        <th rowSpan={2}>Nama</th>
                        <th rowSpan={2}>Bagian</th>
                        <th rowSpan={2}>Kepala</th>
                        <th rowSpan={2} className="lr-center lr-col-line">
                          S
                        </th>
                        <th rowSpan={2} className="lr-center lr-col-line">
                          I
                        </th>
                        <th rowSpan={2} className="lr-center lr-col-line">
                          A
                        </th>
                        <th colSpan={2} className="lr-center lr-col-line">
                          Setengah hari
                        </th>
                      </tr>
                      <tr>
                        <th className="lr-center lr-col-line lr-subhead">S</th>
                        <th className="lr-center lr-subhead">I</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((ele, index) => (
                        <tr key={ele.name + index}>
                          <td className="lr-center">{index + 1}.</td>
                          <td className="lr-name">{ele.name}</td>
                          <td>{ele.bagian}</td>
                          <td>{ele.kepala}</td>
                          <td className="lr-center lr-col-line">
                            {ele.hariSakit > 0 ? ele.hariSakit : ""}
                          </td>
                          <td className="lr-center lr-col-line">
                            {ele.hariIzin > 0 ? ele.hariIzin : ""}
                          </td>
                          <td className="lr-center lr-col-line">
                            {ele.alpha > 0 ? ele.alpha : ""}
                          </td>
                          <td className="lr-center lr-col-line">
                            {ele.setengahHariSakit > 0
                              ? ele.setengahHariSakit
                              : ""}
                          </td>
                          <td className="lr-center">
                            {ele.setengahHariIzin > 0
                              ? ele.setengahHariIzin
                              : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="lr-empty">
                    Tidak ada data rekapitulasi untuk filter ini.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ListRequest;
